import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/api';

interface AdminSettings {
  usdToGbp: number;
  usdToEur: number;
  articleFixedCostCents: number;
  importFixedCostCents: number;
  basePerWordMicrocents: number;
  audioPerWordMicrocents: number;
  translationFixedCostCents: number;
  welcomeCreditCents: number;
  minDepositCents: number;
  maxDepositCents: number;
  maxImportWords: number;
  minArticleWords: number;
  maxArticleWords: number;
  maxChunkWords: number;
  updatedAt: string;
}

type Tab = 'fx' | 'pricing' | 'limits';

const SettingsDialog: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState<Tab>('fx');

  useEffect(() => {
    if (!token) return;
    adminFetch<{ settings: AdminSettings }>('/admin/settings', token)
      .then(data => setSettings(data.settings))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || !settings) return;

    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const { updatedAt: _, ...body } = settings;
      const data = await adminFetch<{ settings: AdminSettings }>('/admin/settings', token, {
        method: 'PUT',
        body,
      });
      setSettings(data.settings);
      setSuccess('Settings saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const update = (field: keyof AdminSettings, value: number) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
    setSuccess('');
  };

  const num = (field: keyof AdminSettings, label: string, opts: { step?: string; min?: number; max?: number; col?: number } = {}) => {
    const { step, min = 0, max = 100000, col = 6 } = opts;
    return (
      <div className={`col-${col}`}>
        <label className="form-label small">{label}</label>
        <input type="number" className="form-control form-control-sm"
          step={step} min={min} max={max}
          value={settings![field] as number}
          onChange={e => update(field, step ? parseFloat(e.target.value) : parseInt(e.target.value))} />
      </div>
    );
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title">Settings</h6>
            <button type="button" className="btn-close btn-close-sm" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading && (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            )}

            {!loading && !settings && (
              <div className="alert alert-danger py-2 small mb-0">{error || 'Failed to load settings'}</div>
            )}

            {!loading && settings && (
              <>
                {settings.updatedAt && (
                  <p className="text-muted small mb-2">
                    Last updated: {new Date(settings.updatedAt).toLocaleString()}
                  </p>
                )}

                {error && <div className="alert alert-danger py-1 small">{error}</div>}
                {success && <div className="alert alert-success py-1 small">{success}</div>}

                <ul className="nav nav-tabs mb-3">
                  {([['fx', 'FX Rates'], ['pricing', 'Pricing'], ['limits', 'Limits']] as [Tab, string][]).map(([id, label]) => (
                    <li className="nav-item" key={id}>
                      <button className={`nav-link ${tab === id ? 'active' : ''}`}
                        onClick={() => setTab(id)} type="button">{label}</button>
                    </li>
                  ))}
                </ul>

                <form onSubmit={handleSubmit}>
                  {tab === 'fx' && (
                    <div className="row g-2">
                      {num('usdToGbp', 'USD to GBP', { step: '0.001', min: 0.01, max: 50 })}
                      {num('usdToEur', 'USD to EUR', { step: '0.001', min: 0.01, max: 50 })}
                    </div>
                  )}

                  {tab === 'pricing' && (
                    <div className="row g-2">
                      {num('articleFixedCostCents', 'Article fixed (cents)', { max: 10000 })}
                      {num('importFixedCostCents', 'Import fixed (cents)', { max: 10000 })}
                      {num('basePerWordMicrocents', 'Base per-word (microcents)')}
                      {num('audioPerWordMicrocents', 'Audio per-word (microcents)')}
                      {num('translationFixedCostCents', 'Translation fixed cost (cents)', { max: 10000 })}
                    </div>
                  )}

                  {tab === 'limits' && (
                    <div className="row g-2">
                      {num('welcomeCreditCents', 'Welcome credit (cents)', { max: 1000000, col: 4 })}
                      {num('minDepositCents', 'Min deposit (cents)', { min: 1, max: 1000000, col: 4 })}
                      {num('maxDepositCents', 'Max deposit (cents)', { min: 1, max: 10000000, col: 4 })}
                      {num('minArticleWords', 'Min article words', { min: 10, max: 10000, col: 4 })}
                      {num('maxArticleWords', 'Max article words', { min: 100, max: 100000, col: 4 })}
                      {num('maxChunkWords', 'Max chunk words', { min: 20, max: 500, col: 4 })}
                      {num('maxImportWords', 'Max import words', { min: 100, max: 1000000, col: 4 })}
                    </div>
                  )}

                  <div className="d-flex justify-content-end mt-3">
                    <button type="button" className="btn btn-sm btn-secondary me-2" onClick={onClose}>Close</button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsDialog;
